import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useApi } from '../hooks/useApi';
import type { DataFreshnessRow } from '../types';
import AccountMenu from './AccountMenu';

/** One entry in the sidebar. `id` stays a plain string rather than a union of
 *  the PAGES keys: App.jsx owns that map, and a nav that could not name a page
 *  App has not registered yet would be a constraint this file cannot honour. */
export interface NavTab {
  id: string;
  label: string;
  /** Renders the padlock. The GATE is server-side (`requirePurchasingView`);
   *  this flag only marks the item. */
  confidential?: boolean;
}

export interface NavSection {
  label: string;
  tabs: NavTab[];
}

// Sidebar nav — ids match PAGES in App.jsx. Grouping mirrors app-commercial.
export const SECTIONS: NavSection[] = [
  { label: 'Overview', tabs: [
    { id: 'spend', label: 'Spend Cube' },
  ]},
  { label: 'Intelligence', tabs: [
    { id: 'price', label: 'Price Intelligence' },
    { id: 'brief', label: 'Supply Review Brief' },
  ]},
  { label: 'Suppliers', tabs: [
    { id: 'relationships', label: 'Relationships' },
    { id: 'supplier360', label: 'Supplier 360' },
    { id: 'performance', label: 'Performance & Risk' },
  ]},
  { label: 'Negotiation', tabs: [
    { id: 'register', label: 'Price-Change Register' },
    { id: 'playbooks', label: 'Playbooks', confidential: true },
  ]},
  { label: 'Sourcing', tabs: [
    { id: 'pipeline', label: 'Sourcing Pipeline' },
    // You sign a contract, then you measure coverage of it — so the register
    // sits above the coverage view it feeds.
    { id: 'contracts-register', label: 'Contracts' },
    { id: 'contracts', label: 'Contract Coverage' },
  ]},
  { label: 'System', tabs: [
    { id: 'governance', label: 'Governance' },
    { id: 'data-sources', label: 'Data Sources' },
  ]},
];

const ALL_TABS: NavTab[] = SECTIONS.flatMap((s) => s.tabs);

export interface LayoutProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  /** Whatever `data-theme` holds — App reads it off the document element, so
   *  'light' | 'dark' would be a claim about the DOM nothing here checks. */
  theme: string;
  onToggleTheme: () => void;
  copilotOpen: boolean;
  onToggleCopilot: () => void;
  copilot: ReactNode;
  children?: ReactNode;
}

export default function Layout({
  activeTab, onTabChange, theme, onToggleTheme,
  copilotOpen, onToggleCopilot, copilot, children,
}: LayoutProps) {
  const apiFetch = useApi();

  // Real data freshness from purchasing.JsonStore — the data's "as of" time,
  // not the client's reload time. Headline = newest blob; tooltip lists each.
  const { data: freshness } = useQuery({
    queryKey: ['data-freshness'],
    queryFn: async (): Promise<DataFreshnessRow[]> => {
      const res = await apiFetch('/data-freshness');
      if (!res.ok) throw new Error(`freshness ${res.status}`);
      return res.json() as Promise<DataFreshnessRow[]>;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // `updatedAt` is `unknown` in types.ts because a raw DATETIME2 only becomes
  // text inside the API's response serialiser and nothing checks it on either
  // side. The assertion is what `new Date` does anyway — it coerces whatever it
  // is handed — and an unparseable value has always fallen through to
  // "Invalid Date" here rather than to the em dash, so nothing is claimed that
  // changes what this header renders.
  const dataAsOf = freshness?.length
    ? new Date(Math.max(...freshness.map((r) => new Date(r.updatedAt as string | number | Date).getTime())))
    : null;
  const freshnessTitle = freshness?.length
    ? freshness.map((r) => `${r.key}: ${new Date(r.updatedAt as string | number | Date).toLocaleString('en-AU')}`).join('\n')
    : 'Per-dataset refresh times';

  const activeLabel = ALL_TABS.find((t) => t.id === activeTab)?.label ?? '';

  return (
    <div className="shell">
      <nav className="sidebar" style={{ width: 224 }} aria-label="Sections">
        <div className="sidebar-brand">
          <img src="/majans-logo.png" alt="" />
          <div>
            <div className="bt">Purchasing</div>
            <div className="bs">Majans · buy side</div>
          </div>
        </div>

        {SECTIONS.map((section) => (
          <div key={section.label}>
            <div className="nav-group">{section.label}</div>
            {section.tabs.map((tab) => (
              <button
                key={tab.id}
                className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => onTabChange(tab.id)}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                <span className="nav-dot" />
                <span>{tab.label}</span>
                {tab.confidential && (
                  <span className="nav-lock" title="Confidential — access-controlled">🔒</span>
                )}
              </button>
            ))}
          </div>
        ))}
        <div style={{ flex: 1 }} />
      </nav>

      <div className="main-col">
        <header className="topbar">
          <h1>{activeLabel}</h1>
          <div className="spacer" />
          <span className="freshness" title={freshnessTitle}>
            {dataAsOf
              ? `Data as of ${dataAsOf.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`
              : 'Data as of —'}
          </span>
          <button
            className={copilotOpen ? 'btn btn-ai' : 'btn'}
            onClick={onToggleCopilot}
            aria-pressed={copilotOpen}
          >
            ✦ Copilot
          </button>
          <button className="btn" onClick={onToggleTheme} title="Toggle light / dark">
            {theme === 'dark' ? '☀' : '☾'}
          </button>
          <AccountMenu />
        </header>

        <div className={copilotOpen ? 'content-with-copilot' : ''}>
          <main className="content">{children}</main>
          {copilotOpen && copilot}
        </div>
      </div>
    </div>
  );
}
